import { startTransition, useCallback, useEffect, useRef, useState } from "react";
import { Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "./Button";
import Checkbox from "./Checkbox";
import DefaultButton from "./DefaultButton";
import { FloatingLabel } from "./FloatingLabel";
import { Col, Row } from "./Grid";
import Icon from "./Icon";
import PasswordInput from "./PasswordInput";
import { PasswordResetCtrl } from "./PasswordReset";
import ReactForm from "./ReactForm";
import TextInput from "./TextInput";
import useClientConfig from "../hooks/clientConfigHook";
import useNetwork from "../hooks/networkHook";
import { nowISOString } from "../utils/dateUtils";
import { Modal, ShowMessageDialog } from "../utils/dialogUtlities";
import { safeHtml } from "../utils/safeHtml";
import { signIn } from "../reducers/initContext";
import { fetch } from "../utils/fetch";
import { useApp } from "./AppProvider";
import { persistor } from "../../store";
import { useMemo } from "react";

const TermsDialog = ({ show, onClose }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const [read, setRead] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const observer = useRef();
    const containerRef = useRef();
    const loadingRef = useRef();

    useEffect(() => setRead(false), [clientConfig?.terms])

    const handleObserver = useCallback(entries => {
        const target = entries[0];
        if (target.isIntersecting) {
            setRead(true);
        }
    }, []);

     // Setup and cleanup the observer
    useEffect(() => {
        if (loadingRef.current) {
            observer.current = new IntersectionObserver(handleObserver, {
                root: containerRef.current, // assuming viewport as root
                rootMargin: '10px',
                threshold: 0.1
            });
            observer.current.observe(loadingRef.current);
        }

        return () => {
            if (observer.current) observer.current.disconnect();
        };
    }, [loadingRef, handleObserver, clientConfig.terms]);

    return <Modal show={show} size="lg">
        <Modal.Header>
            <Modal.Title>{t('userAgreement')}</Modal.Title>
        </Modal.Header>
        <Modal.Body ref={containerRef} className="d-felx flex-column" style={{ height: 'calc(100dvh - 180px)', overflowY: 'auto' }}>
            <div dangerouslySetInnerHTML={{ __html: clientConfig?.terms }}></div>
            <div ref={loadingRef}>&nbsp;</div>
        </Modal.Body>
        <Modal.Footer>
            <Row className="w-100">
                <Col xs="auto">
                    <Checkbox checked={accepted} hideLabel={true} disabled={!read} onChange={setAccepted} placeholder={t('termsAccept')}></Checkbox>
                </Col>
                <Col xs></Col>
                <Col xs="auto">
                    <Button className="mx-1" variant="danger" onClick={() => onClose(false)}><Icon intent="cancel"></Icon> {t('reject')}</Button>
                </Col>
                <Col xs="auto">
                    <Button variant="success" disabled={!accepted || !read} onClick={() => onClose(true)}><Icon intent="ok"></Icon> {t('accept')}</Button>
                </Col>
            </Row>
        </Modal.Footer>
    </Modal>
}

const Signin = ({ authService }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const network = useNetwork();
    const App = useApp();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [ searchParams ] = useSearchParams();
    const { target, error: inputError } = Object.fromEntries(searchParams?.entries() ?? []);
    const [username, setUsername] = useState();
    const [password, setPassword] = useState();
    const [accessCode, setAccessCode] = useState();
    const [resetInfo, setResetInfo] = useState({});
    // states: initial -> mfa (if needed) -> reset (if needed) -> terms (if needed)
    const [state, setState] = useState('initial');
    const [error, setError] = useState(inputError ? decodeURIComponent(inputError) : undefined);
    const formRef = useRef();
    const focusRef = useRef();
    const token = useRef();

    const changeState = state => {
        setState(state);
        setError();
    }
    
    useEffect(() => {
        if (App.isApp) {
            App.emit('awaitingSignin');
            const cleanup = App.on('authenticate', data => {
                startTransition(() => {
                    signIn(App, dispatch, navigate, data.token);
                    navigate('/')
                })
            })
            return () => cleanup();
        }
    }, [App, dispatch, navigate])

    useEffect(() => {
        focusRef.current?.focus();
    }, [state])

    const clearState = useCallback(() => {
        changeState('initial');
        setUsername();
        setPassword();
        setAccessCode();
        token.current = undefined;
    }, []);

    const doFetch = async(url, { body, ...options } = {}) => {
        const config = {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: token.current
            },
            ...options
        }
        if (body) {
            config.body = JSON.stringify(body);
        }
        const response = await fetch(url, config);
        if (response.ok) {
            return await response.json();
        }
        return null;
    }

    const submit = useCallback(async (value) => {
        if (!formRef.current || formRef.current?.checkValidity()) {
            if (state === 'initial') {
                if (!accessCode) {
                    if (clientConfig?.ssoDomains?.some(domain => username.toLowerCase().endsWith('@' + domain))) {
                        await persistor?.purge();
                        window.location.assign('/login?username=' + encodeURIComponent(username.toLowerCase()) + (target ? '&target=' + encodeURIComponent(target) : ''));
                        return;
                    } else {
                        const res = await network.post('/mfa', { username });
                        if (!res) {
                            setError(t('authenticationFailure'));
                        }
                        if (res.mfa_required) {
                            changeState('mfa')
                            return;
                        }
                    }
                }
            }
            if (state === 'initial' || state === 'mfa') {
                const res = await network.post('/authenticate', { username, password, accessCode });
                if (res?.type === true) {
                    token.current = res.token;
                    if (res.reset_required) {
                        changeState('reset');
                        return;
                    }
                    if (clientConfig.terms && !res.termsAccepted) {
                        changeState('terms');
                        return;
                    }
                    // fall down to success
                } else if (res?.type === false) {
                    clearState();
                    setError(res.data);
                    return;
                } else {
                    clearState();
                    setError(t('authenticateFailure'));
                    return;
                }
            }
            if (state === 'reset') {
                const result = await doFetch('/api/v1/changePassword', { method: 'POST', body: { currentPassword: password, newPassword: resetInfo.password } });
                if (result) {
                    changeState('reset_success');
                    return;
                } else {
                    clearState();
                    setError(t('authenticationFailure'))
                    return;
                }
            }
            if (state === 'terms') {
                if (value) {
                    const result = await doFetch('/api/v1/userSettings', { method: 'POST', body: { termsAccepted: nowISOString() } });
                    if (result) {
                        changeState('initial');
                        // fall through to success
                    } else {
                        clearState();
                        setError(t('authenticationFailure'));
                        return;
                    }
                } else {
                    clearState();
                    return;
                }
            }

            if (token.current) {
                // success - pass the token on and redirect
                if (authService) {
                    authService.login(token.current);
                    window.location.hash = '#!/cad';
                } else {
                    signIn(App, dispatch, navigate, token.current);
                    navigate('/')
                }
            }
        }
    }, [t, resetInfo.password, username, password, accessCode, network, state, clearState, authService, target, clientConfig?.ssoDomains, clientConfig?.terms, navigate, dispatch, App]);

    const keyPress = useCallback(event => {
        if (event.key === 'Enter') {
            submit();
        }
    }, [submit])

    const imgUrl = useMemo(() => {
        switch (clientConfig.branding) {
            case 'medstat':
                return '/images/medstat.png';
            case 'locator':
                return '/images/locator.png';
            default:
                return '/images/chronicler.png';
        }
    }, [clientConfig.branding]);

    return <>
        { state === 'terms' && <TermsDialog show={true} onClose={submit}></TermsDialog> }
        <ShowMessageDialog showModal={state === 'reset_success'} title={t('passwordChanged')} message={t('passwordChangeSuccess')} onClose={clearState}></ShowMessageDialog>
        <div className="my-4" style={{ margin: 'auto', maxWidth: '300px' }} onKeyUp={keyPress}>
            <Row className="mb-4">
                <Col>
                    <img alt="" src={imgUrl} style={{ maxWidth: '300px' }}></img>
                </Col>
            </Row>
            { error && <Row>
                <Col>
                    <FloatingLabel>
                        <Alert variant="danger" className="mb-0">
                            {error}
                        </Alert>
                    </FloatingLabel>
                </Col>
            </Row> }
            { state === 'initial' && <ReactForm ref={formRef}>
                <Row>
                    <Col>
                        <FloatingLabel>
                            <TextInput ref={focusRef} value={username} hideLabel placeholder={t('username')} live onChange={setUsername} maxLength={256} required></TextInput>
                        </FloatingLabel>
                    </Col>
                </Row>
                { (!username || (clientConfig?.ssoDomains ?? []).every(domain => !username.toLowerCase().endsWith('@' + domain))) && <Row>
                    <Col>
                        <FloatingLabel>
                            <PasswordInput value={password} hideLabel placeholder={t('password')} live onChange={setPassword} required></PasswordInput> 
                        </FloatingLabel>
                    </Col>
                </Row> }
                <Row>
                    <Col className="px-1">
                        <FloatingLabel>
                            <Button className="btn-chrono w-100" variant="solid-primary" onClick={submit}>{t('signIn')}</Button>
                        </FloatingLabel>
                    </Col>
                </Row>
                <Row className="mt-4 text-center">
                    <Col>
                        <Button variant="link" style={{ color: '#dfdfdf' }} onClick={() => startTransition(() => navigate('/forgottenPassword'))}>{t('forgottenPassword')}</Button>
                    </Col>
                </Row>
                { clientConfig?.accountRequest?.enable && <Row className="mt-2 text-center">
                    <Col>
                        <Button variant="link" style={{ color: '#dfdfdf' }} onClick={() => startTransition(() => navigate('/requestAccount'))}>{t('accountRequest_one')}</Button>
                    </Col>
                </Row> }
            </ReactForm> }
            { state === 'mfa' && <ReactForm ref={formRef}>
                <Row>
                    <Col style={{ color: 'white' }}>
                        {t('mfaInstruction')}
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <FloatingLabel>
                            <TextInput ref={focusRef} value={accessCode} required hideLabel placeholder={t('verificationCode')} live onChange={setAccessCode}></TextInput>
                        </FloatingLabel>
                    </Col>
                </Row>
                <Row>
                    <Col xs></Col>
                    <Col xs="auto">
                        <FloatingLabel>
                            <DefaultButton kind="clear" variant="solid-danger" onClick={clearState}></DefaultButton>
                        </FloatingLabel>
                    </Col>
                    <Col xs="auto">
                        <FloatingLabel>
                            <Button className="btn-chrono w-100" variant="solid-primary" onClick={submit}>{t('submit')}</Button>
                        </FloatingLabel>
                    </Col>
                </Row>
            </ReactForm> }
            { state === 'reset' && <ReactForm ref={formRef}>
                <Row>
                    <Col style={{ color: 'white' }}>
                        {t('passwordChangeRequired')}
                    </Col>
                </Row>
                <PasswordResetCtrl ref={focusRef} value={resetInfo} onChange={delta => setResetInfo(info => ({ ...info, ...delta }))} requireCurrent={false}></PasswordResetCtrl>
                <Row>
                    <Col xs></Col>
                    <Col xs="auto">
                        <FloatingLabel>
                            <DefaultButton kind="clear" variant="solid-danger" onClick={clearState}></DefaultButton>
                        </FloatingLabel>
                    </Col>
                    <Col xs="auto">
                        <FloatingLabel>
                            <Button className="btn-chrono w-100" variant="solid-primary" onClick={submit}>{t('resetPassword')}</Button>
                        </FloatingLabel>
                    </Col>
                </Row>
            </ReactForm>}
        </div>
    </>
}
export default Signin;